import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Aurora from '../components/Aurora';
import Logo from '../components/Logo';
import { FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: boolean;
    email?: boolean;
    password?: boolean;
    confirmPassword?: boolean;
  }>({});
  const navigate = useNavigate();

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  const validateFields = () => {
    const errors: typeof fieldErrors = {};
    let hasError = false;

    if (!name.trim()) {
      errors.name = true;
      hasError = true;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = true;
      hasError = true;
    }
    if (!password || password.length < 6) {
      errors.password = true;
      hasError = true;
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = true;
      hasError = true;
    }

    setFieldErrors(errors);
    return !hasError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateFields()) {
      setError('Please fill in all fields correctly');
      return;
    }

    setIsLoading(true);

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Registration failed');
        return;
      }

      localStorage.setItem('token', data.token);
      window.dispatchEvent(new CustomEvent('authChange', { detail: { isAuthenticated: true } }));
      navigate('/projects');
    } catch (error) {
      console.error('Registration error:', error);
      setError('An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength calculation
  const passwordStrength = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][passwordStrength];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#059669'][passwordStrength];

  const inputBaseClass = (hasError: boolean) =>
    `w-full rounded-xl border-2 px-4 py-3.5 text-sm font-medium transition-all duration-200 placeholder:font-normal focus:outline-none focus:ring-0 ${
      hasError
        ? 'border-red-400 bg-red-50/50 text-red-700 placeholder:text-red-400/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)] dark:border-red-500/50 dark:bg-red-500/5 dark:text-red-400'
        : 'border-gray-200 bg-gray-50/80 text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] dark:border-gray-600 dark:bg-gray-700/50 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400'
    }`;

  return (
    <div className="relative flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900 lg:flex-row" style={{ fontFamily: "'Geist', sans-serif" }}>
      {/* Aurora Background */}
      <div className="absolute inset-0 z-[1] bg-black text-white [&_canvas]:!w-full">
        <Aurora
          colorStops={["#0d9488", "#6366f1", "#d946ef"]}
          amplitude={0.9}
          blend={0.5}
          speed={0.4}
        />
      </div>

      {/* Left Panel - Hero */}
      <div
        className="relative z-[2] flex items-center justify-center p-8 text-center text-white lg:flex-1 lg:p-12"
        style={{
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="max-w-[480px]">
          <div className="mb-6 flex justify-center lg:mb-8">
            <Logo size="xl" className="cursor-default hover:translate-y-0 drop-shadow-[0_4px_20px_rgba(0,0,0,0.3)]" />
          </div>
          <h1
            className="mb-4 text-4xl font-bold leading-[1.1] tracking-[-1.5px] lg:text-6xl"
            style={{ fontFamily: "'Playfair Display', serif", textShadow: '0 4px 30px rgba(0,0,0,0.3)' }}
          >
            Start Creating
          </h1>
          <p className="mx-auto max-w-[360px] text-base leading-relaxed text-white/80 lg:text-lg">
            Join thousands of designers building amazing visuals with our editor
          </p>
          <div className="mt-8 hidden items-center justify-center gap-2 lg:flex">
            <div className="h-1.5 w-1.5 rounded-full bg-white/25" />
            <div className="h-1.5 w-8 rounded-full bg-white/40" />
            <div className="h-1.5 w-1.5 rounded-full bg-white/25" />
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="relative z-[2] flex flex-1 items-center justify-center p-6 lg:p-8">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[420px] rounded-2xl border border-white/10 bg-white/95 p-8 shadow-[0_30px_80px_rgba(0,0,0,0.25)] backdrop-blur-xl dark:border-gray-700/50 dark:bg-gray-800/95 lg:rounded-3xl lg:p-10"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.98)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
          }}
        >
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo className="cursor-default hover:translate-y-0 scale-125" />
          </div>

          <h2 className="mb-1 text-center text-3xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white">
            Create Account
          </h2>
          <p className="mb-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Fill in your details to get started
          </p>

          {/* Error Banner */}
          {error && (
            <div
              className="mb-6 flex items-center gap-3 rounded-xl bg-red-50 px-4 py-3 dark:bg-red-500/10"
              style={{ animation: 'authShake 0.4s ease-in-out' }}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                <svg className="h-3 w-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Name Field */}
          <div
            className="mb-4"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.25s',
            }}
          >
            <label htmlFor="name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              placeholder="John Doe"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors(prev => ({ ...prev, name: false }));
                setError('');
              }}
              aria-label="Full name"
              aria-invalid={fieldErrors.name}
              className={inputBaseClass(!!fieldErrors.name)}
              required
            />
          </div>

          {/* Email Field */}
          <div
            className="mb-4"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.3s',
            }}
          >
            <label htmlFor="email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors(prev => ({ ...prev, email: false }));
                setError('');
              }}
              aria-label="Email address"
              aria-invalid={fieldErrors.email}
              className={inputBaseClass(!!fieldErrors.email)}
              required
            />
          </div>

          {/* Password Field */}
          <div
            className="mb-4"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.35s',
            }}
          >
            <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder="Min. 6 characters"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors(prev => ({ ...prev, password: false }));
                  setError('');
                }}
                aria-label="Password"
                aria-invalid={fieldErrors.password}
                className={`${inputBaseClass(!!fieldErrors.password)} pr-12`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
            {/* Password Strength Indicator */}
            {password && (
              <div className="mt-2.5 flex items-center gap-3">
                <div className="flex flex-1 gap-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className="h-[3px] flex-1 rounded-full transition-all duration-500"
                      style={{
                        backgroundColor: level <= passwordStrength ? strengthColor : 'rgba(0,0,0,0.08)',
                      }}
                    />
                  ))}
                </div>
                <span className="text-[11px] font-semibold transition-colors" style={{ color: strengthColor }}>
                  {strengthLabel}
                </span>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div
            className="mb-6"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s',
            }}
          >
            <label htmlFor="confirmPassword" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldErrors(prev => ({ ...prev, confirmPassword: false }));
                  setError('');
                }}
                aria-label="Confirm password"
                aria-invalid={fieldErrors.confirmPassword}
                className={`${inputBaseClass(!!fieldErrors.confirmPassword)} pr-12`}
                required
              />
              {/* Match indicator inside input */}
              {confirmPassword && password && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {confirmPassword === password ? (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
                      <FaCheck size={9} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                  ) : (
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                      <svg className="h-2.5 w-2.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              )}
            </div>
            {confirmPassword && password && confirmPassword !== password && (
              <p className="mt-1.5 text-xs font-medium text-red-500">Passwords don't match</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl border-none px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(13,148,136,0.35)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: 'linear-gradient(135deg, #0d9488, #6366f1, #a855f7)',
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ transform: 'skewX(-20deg)' }} />
            {isLoading ? (
              <>
                <div className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white" style={{ animation: 'spin 0.6s linear infinite' }} />
                <span>Creating Account...</span>
              </>
            ) : (
              <span>Create Account</span>
            )}
          </button>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">Already have an account? </span>
            <Link
              to="/"
              className="text-sm font-semibold text-indigo-500 transition-colors hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes authShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Register;
